import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppBranding } from '../../../../config/branding';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  RequestTransactionsInterFace,
  TransactionsInterface,
} from '../../../../interface/transactions-station-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  disableTransactionFiltersApplied,
  fetchTransactions,
  fetchTransactionsFilters,
  updateTransactionsFilters,
  setSortData,
} from '../../../../redux/slices/TransactionsStationSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatUtcString from '../../../../shared-functions/FormatUtcString';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import stationManagementApi from '../../../../utils/api/station-management';

import TransactionCharts from './TransactionCharts';

/**
 * Transaction component
 * @returns {any} The rendered component
 */
function Transactions() {
  const branding = useAppBranding();
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const {
    transactions,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.transactions);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);

  const column = useMemo(
    () => [
      {
        label: t('date'),
        accessorKey: 'createdAt' as keyof TransactionsInterface,
        sortAllow: true,
        /**
         * Converting timestamp to UTC date
         * @param {TransactionsInterface} stationActivity The activity object
         * @returns {any} The formatted date element
         */
        customData: (stationActivity: TransactionsInterface) => (
          <p>{ConvertUTCtoDate(stationActivity.createdAt)}</p>
        ),
      },
      {
        label: t('time'),
        accessorKey: 'time' as keyof TransactionsInterface,
        sortAllow: true,
        /**
         * Converting timestamp to UTC time
         * @param {TransactionsInterface} stationActivityTime The activity object
         * @returns {any} The formatted time element
         */
        customData: (stationActivityTime: TransactionsInterface) => (
          <p>{FormatUtcString(ConvertUTCtoHHMM(stationActivityTime.createdAt))}</p>
        ),
      },
      {
        label: t('userName'),
        accessorKey: 'username' as keyof TransactionsInterface,
        sortAllow: true,
      },
      {
        label: t('connector'),
        accessorKey: 'connector' as keyof TransactionsInterface,
      },
      {
        label: t('amount'),
        accessorKey: 'foRevenue' as keyof TransactionsInterface,
        sortAllow: true,
        /**
         * KWh Consumed
         */
        customData: (currentRow: TransactionsInterface) => {
          return (
            <p>
              {ToFixedValue(currentRow.foRevenue)} {branding.text.currency}
            </p>
          );
        },
      },
      {
        label: t('paymentStatus'),
        accessorKey: 'paymentStatus' as keyof TransactionsInterface,
        /**
         * Converting timestamp to UTC time
         * @param {TransactionsInterface} stationActivity The activity object
         * @returns {any} The payment status element
         */
        customData: (stationActivity: TransactionsInterface) => {
          const type = stationActivity.paymentStatus?.toLowerCase();

          return (
            <p className={`${type === 'processed' ? 'type-in' : 'type-out'}`}>
              {type === 'processed' ? t('paid') : t('failed')}
            </p>
          );
        },
      },
      {
        label: t('energyConsumed'),
        accessorKey: 'powerConsumed' as keyof TransactionsInterface,
        sortAllow: true,
        /**
         * KWh Consumed
         */
        customData: (currentRow: TransactionsInterface) => {
          const value = Number(currentRow?.powerConsumed) / 1000;
          return (
            <p>
              {ToFixedValue(value)} {t('kwh')}
            </p>
          );
        },
      },
    ],
    [t],
  );
  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('time'), accessorKey: 'time' },
    { label: t('userName'), accessorKey: 'username' },
    { label: t('connector'), accessorKey: 'connector' },
    { label: t('amount'), accessorKey: 'foRevenue' },
    { label: t('paymentStatus'), accessorKey: 'paymentStatus' },
    { label: t('energyConsumed'), accessorKey: 'powerConsumed' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function for pdf and csv
   * @param {string} type pdf or csv
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    stationManagementApi
      .fetchTransactionListExport({ chargingStation: selectedChargingPoint?.id })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: TransactionsInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'createdAt':
                return ConvertUTCtoDate(row.createdAt) || 'N/A';
              case 'time':
                return ConvertUTCtoHHMM(row.createdAt) || 'N/A';
              case 'foRevenue':
                return `${ToFixedValue(row.foRevenue) || '0.00'} ${branding.text.currency}`;
              case 'powerConsumed':
                return `${ToFixedValue(Number(row?.powerConsumed) / 1000)} ${t('kwh')}` || '0.00';
              case 'paymentStatus':
                return row.paymentStatus === 'processed' ? 'Paid' : 'Failed';
              default:
                return (row as any)[col.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'charging-station-approved.csv');
        } else {
          exportPDF(headers, rows, 'charging-station-approved.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the fetch the data in api function
   * @param {RequestTransactionsInterFace} data The request data
   */
  const fetchData = (data: RequestTransactionsInterFace) => {
    if (!loading && selectedChargingPoint) {
      dispatch(fetchTransactions({ ...data, chargingStation: selectedChargingPoint?.id }));
    }
  };
  /**
   * Table pagination function
   * @param {any} value The pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  /**
   * Function for sorting table data
   * @param {any} sortData The sort data
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit,
      });
      dispatch(disableTransactionFiltersApplied());
    }
  }, [filters, selectedChargingPoint]);

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    const currentId = selectedChargingPoint.id;
    const lastId = sessionStorage.getItem('TransactionsFiltersList'); // string | null

    if (lastId !== currentId || filters.length === 1) {
      dispatch(fetchTransactionsFilters({ chargingStation: currentId }));
    }

    if (transactions.length === 0 || lastId !== currentId) {
      fetchData({
        page: DefaultApiLimit.page,
        limit,
      });
      dispatch(disableTransactionFiltersApplied());
    }
    sessionStorage.setItem('TransactionsFiltersList', currentId);
  }, [selectedChargingPoint?.id, filters]);
  return (
    <div className="transaction-wrap" dir={i18n.dir()}>
      <div className="transaction-right-content">
        <CustomTable
          header={column}
          data={transactions}
          serialNo
          version="v2"
          sizeChangeButtonRequired
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          filterListLoading={filterListLoading}
          filters={filters}
          filtersToDispatch={updateTransactionsFilters}
          filterListToDispatch={fetchTransactionsFilters}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          ReferenceCurrentPages={handleRefresh}
          pageLimitChange={pageNateLimitChange}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          onHiddenColumnsChange={setHiddenColumns}
        />
      </div>
      <div className="transaction-left-content">
        <TransactionCharts />
      </div>
    </div>
  );
}

export default Transactions;
